/** @deprecated use `AccountState` from `account-state.enum.ts`. */
export enum WarmupStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  THROTTLED = 'THROTTLED',
  MAINTENANCE = 'MAINTENANCE',
  PAUSED = 'PAUSED',
}

/**
 * Statuses where the account actively schedules sends (subject to volume).
 * Used by the scheduler/picker to filter eligible accounts.
 */
export const SENDING_STATUSES: ReadonlyArray<WarmupStatus> = [
  WarmupStatus.RUNNING,
  WarmupStatus.THROTTLED,
  WarmupStatus.MAINTENANCE,
];

/**
 * Statuses that consume a slot against the org's `maxEmailAccounts` plan cap.
 *
 * Deliberately ONE home shared across apps, because the CASCADE that pauses the
 * overflow on a plan downgrade (warmup-worker `pauseDowngradeOverflow`) and the
 * GATES that stop an account re-entering an actively-warming state (api
 * `enableWarmup` / `resumeWarmup` / bulk enable, warmup-worker
 * `promoteFromDnsPass`) must count the SAME set. If they drift, the gate admits
 * accounts the cascade would have paused — silently, since neither side errors
 * and the only symptom is an org warming above its plan for as long as nobody
 * looks. Never re-declare this list at a call site.
 *
 * Currently identical in MEMBERSHIP to `SENDING_STATUSES` — it previously held
 * one extra member (`GRADUATED`, removed when that never-written state was
 * deleted). Keep the two constants SEPARATE anyway: they answer different
 * questions ("costs a plan slot" vs "sends mail"), and the six plan-cap
 * enforcement points are built on this one being the cap's list. Collapsing
 * them would silently re-couple two rules that are free to diverge again.
 *
 * NOTE what is NOT here: `PENDING`. A connected-but-not-yet-promoted mailbox
 * costs no slot, which is why a downgrade that lands while every account is
 * PENDING pauses nothing — the promotion path is what must hold the line.
 */
export const ACTIVELY_WARMING_STATUSES: ReadonlyArray<WarmupStatus> = [
  ...SENDING_STATUSES,
];

export enum WarmupPoolTier {
  A = 'A',
  B = 'B',
  C = 'C',
}

export enum InteractionType {
  SEND = 'SEND',
  OPEN = 'OPEN',
  FLAG = 'FLAG',
  SPAM_TO_INBOX = 'SPAM_TO_INBOX',
  REPLY = 'REPLY',
  // Thread-continuation (FORWARD, RECATEGORIZE) and bulk-mailbox-op
  // (BULK_READ, BULK_FLAG, BULK_MOVE_TO_INBOX) interaction types. Independent
  // planner capabilities gated by `threadTendency` /
  // `WARMUP_BULK_INJECTION_PERCENT`, not tied to any per-account profile.
  FORWARD = 'FORWARD',
  RECATEGORIZE = 'RECATEGORIZE',
  BULK_READ = 'BULK_READ',
  BULK_FLAG = 'BULK_FLAG',
  BULK_MOVE_TO_INBOX = 'BULK_MOVE_TO_INBOX',
}

export enum InteractionStatus {
  PENDING = 'PENDING',
  // Transient claim state for the mailbox-session task drain: an account's
  // session atomically flips up to 3 oldest PENDING rows to RUNNING
  // (`UPDATE ... WHERE id IN (...) AND status = PENDING`) so a concurrent
  // session never double-executes the same interaction. Set to COMPLETED /
  // FAILED once the executor finishes.
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  // Terminal, SEND-only: the send was claimed and dispatched, but
  // `atomicQuotaDecrement` refused it because the org / workspace / account
  // budget was already spent. Distinct from COMPLETED (the message went out and
  // has a `warmupEmailHistory` row) and from FAILED (the send was attempted and
  // broke): nothing was attempted, no message left the system, and no history
  // row of any kind exists. Before this value such a row was written COMPLETED,
  // which made a quota-starved account indistinguishable in the DB from a
  // healthy sending one — the only trace was a single `quota_blocked` warn line.
  QUOTA_EXHAUSTED = 'QUOTA_EXHAUSTED',
}

export enum BounceType {
  HARD = 'HARD',
  SOFT = 'SOFT',
}

export enum WarmupStrategy {
  SMART = 'SMART',
}

/**
 * Internal band key for a daily limit (<10, 10–19, 20–29, 30–39, 40+), derived
 * at read time from a mailbox's `warmupCurrentLimit` via
 * `warmupLevelFromVolume`. It keys the reply-rate and thread-tendency band
 * tables; it is never user-picked and never persisted as a source of truth.
 * Ordered ascending in `WARMUP_LEVEL_ORDER`.
 *
 * NOTE (conventions §9): the deprecated `emailAccountWarmupConfig.warmupLevel`
 * DB ENUM column still mirrors these values until it is dropped, so a new value
 * here needs a matching `ALTER … MODIFY COLUMN` until then.
 */
export enum WarmupLevel {
  NOVICE = 'NOVICE',
  APPRENTICE = 'APPRENTICE',
  EXPERT = 'EXPERT',
  MASTER = 'MASTER',
  GRANDMASTER = 'GRANDMASTER',
}

/**
 * Account-age + signal-derived classification driving per-account warmup
 * thresholds (Spec §6.2). Per-classification threshold values live as
 * in-memory constants in
 * `apps/warmup-worker/src/modules/classification/threshold-config.service.ts`
 * — there is no DB-backed override.
 *
 * - NEW_DOMAIN  — domain registered < 30 days ago (strictest thresholds)
 * - NEW_MAILBOX — mailbox created < 30 days ago, domain is mature
 * - PENALIZED   — established account whose inbox placement has dropped < 60
 * - ESTABLISHED — domain + mailbox both mature, inbox placement healthy
 */
export enum AccountClassification {
  NEW_DOMAIN = 'NEW_DOMAIN',
  NEW_MAILBOX = 'NEW_MAILBOX',
  PENALIZED = 'PENALIZED',
  ESTABLISHED = 'ESTABLISHED',
}

/**
 * `warmupEmailHistory.emailType` — the tinyint discriminator separating a
 * genuine new send from the terminal artefacts the REPLY / FORWARD executors
 * write. Producer is email-worker (`SendExecutor` / `ReplyExecutor` /
 * `ForwardExecutor`); consumers now live in other apps, which is what makes it
 * an internal cross-app wire value rather than a private literal.
 *
 * Reference these, never a re-typed `1` / `2` / `3`. The three executors each
 * held a private copy (`EMAIL_TYPE_EMAIL` / `EMAIL_TYPE_REPLY` /
 * `EMAIL_TYPE_FORWARD`) and warmup-worker's delivery gate was about to become a
 * fourth — a bare numeric literal at a read site type-checks against `number`
 * forever and drifts in total silence, since a wrong emailType predicate does
 * not error, it just counts the wrong population.
 *
 * `satisfies Record<WarmupEmailTypeName, number>` gives the one-directional
 * check available for a name→number map: a new name in the union with no
 * constant here fails `typecheck`. (It cannot also catch a constant whose value
 * is not a member the way `ACCOUNT_EVENT_TYPE`'s `Record<Union, Union>` does —
 * that shape only works when keys and values are the same string union.)
 */
export type WarmupEmailTypeName = 'SEND' | 'REPLY' | 'FORWARD';

export const WARMUP_EMAIL_TYPE = {
  SEND: 1,
  REPLY: 2,
  FORWARD: 3,
} as const satisfies Record<WarmupEmailTypeName, number>;

export type WarmupEmailType = (typeof WARMUP_EMAIL_TYPE)[WarmupEmailTypeName];

/**
 * The non-send artefact types. A REPLY / FORWARD row carries `sentAt` and is
 * stamped `deliveredAt` at insert time (no session delivery detection runs for
 * a terminal artefact), so any query counting "sends" or "confirmed
 * deliveries" over `warmupEmailHistory` must decide explicitly whether it means
 * this set too — see `DeliveryGateRepository`, whose denominator silently
 * included them.
 */
export const WARMUP_ARTEFACT_EMAIL_TYPES: ReadonlyArray<WarmupEmailType> = [
  WARMUP_EMAIL_TYPE.REPLY,
  WARMUP_EMAIL_TYPE.FORWARD,
];
